import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FAB_STORAGE_KEY,
  clampFabPosition,
  getDefaultFabPosition,
  getLayoutSafeInsets,
  loadStoredFabPosition,
  resolveShellPositionFromFab,
  snapFabToEdge,
} from '../utils/messengerFabUtils';

const createDragState = () => ({
  pointerId: null,
  startX: 0,
  startY: 0,
  originX: 0,
  originY: 0,
  hasMoved: false,
});

export const useMessengerFabPosition = ({ isOpen }) => {
  const [safeInsets, setSafeInsets] = useState(() => getLayoutSafeInsets());
  const [fabPosition, setFabPosition] = useState(() =>
    clampFabPosition(
      loadStoredFabPosition() || getDefaultFabPosition(getLayoutSafeInsets()),
      getLayoutSafeInsets(),
    ),
  );
  const [isFabDragging, setIsFabDragging] = useState(false);
  const fabDragRef = useRef(createDragState());
  const suppressFabClickRef = useRef(false);

  const syncInsetsAndPosition = useCallback((nextInsets) => {
    setSafeInsets(nextInsets);
    setFabPosition((prev) => clampFabPosition(prev, nextInsets));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(
      FAB_STORAGE_KEY,
      JSON.stringify(clampFabPosition(fabPosition, safeInsets)),
    );
  }, [fabPosition, safeInsets]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      syncInsetsAndPosition(getLayoutSafeInsets());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [syncInsetsAndPosition]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (typeof window.ResizeObserver === 'undefined') return undefined;

    const mainScrollContainer = document.getElementById('main-scroll-container');
    const siblingNav = mainScrollContainer?.previousElementSibling;
    const mobileHeader = document.getElementById('mobile-header');
    const desktopHeader = mainScrollContainer?.querySelector?.('header');
    const tracked = [mainScrollContainer, siblingNav, mobileHeader || desktopHeader].filter(Boolean);
    if (!tracked.length) return undefined;

    const refreshInsets = () => {
      syncInsetsAndPosition(getLayoutSafeInsets());
    };

    const observer = new ResizeObserver(refreshInsets);
    tracked.forEach((element) => observer.observe(element));
    refreshInsets();

    return () => observer.disconnect();
  }, [syncInsetsAndPosition]);

  useEffect(() => {
    syncInsetsAndPosition(getLayoutSafeInsets());
  }, [isOpen, syncInsetsAndPosition]);

  useEffect(() => {
    if (!isOpen) return;
    setIsFabDragging(false);
    fabDragRef.current = createDragState();
  }, [isOpen]);

  const handleFabPointerDown = useCallback(
    (event) => {
      if (isOpen) return;
      if (event.button !== 0) return;

      event.preventDefault();

      const origin = clampFabPosition(fabPosition, safeInsets);
      fabDragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: origin.x,
        originY: origin.y,
        hasMoved: false,
      };
      setIsFabDragging(true);
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [fabPosition, isOpen, safeInsets],
  );

  const handleFabPointerMove = useCallback(
    (event) => {
      if (isOpen) return;
      const drag = fabDragRef.current;
      if (drag.pointerId == null || drag.pointerId !== event.pointerId) return;

      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (!drag.hasMoved && Math.hypot(dx, dy) > 4) {
        drag.hasMoved = true;
      }

      setFabPosition(
        clampFabPosition(
          {
            x: drag.originX + dx,
            y: drag.originY + dy,
          },
          safeInsets,
        ),
      );
    },
    [isOpen, safeInsets],
  );

  const handleFabPointerEnd = useCallback(
    (event) => {
      const drag = fabDragRef.current;
      if (drag.pointerId == null || drag.pointerId !== event.pointerId) return;

      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      if (drag.hasMoved) {
        suppressFabClickRef.current = true;
        setFabPosition((prev) => snapFabToEdge(prev, safeInsets));
      }

      fabDragRef.current = createDragState();
      setIsFabDragging(false);
    },
    [safeInsets],
  );

  const consumeSuppressedClick = useCallback(() => {
    if (!suppressFabClickRef.current) return false;
    suppressFabClickRef.current = false;
    return true;
  }, []);

  const shellPosition = useMemo(
    () => resolveShellPositionFromFab(fabPosition, safeInsets),
    [fabPosition, safeInsets],
  );

  return {
    fabPosition,
    isFabDragging,
    shellPosition,
    handleFabPointerDown,
    handleFabPointerMove,
    handleFabPointerEnd,
    consumeSuppressedClick,
  };
};
